import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { StatusBadge, PriorityBadge } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import TaskDialog from "@/components/TaskDialog";
import PaymentDialog from "@/components/PaymentDialog";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Plus, CheckCircle2, Pencil, Trash2, Filter, ShieldAlert, IndianRupee, Receipt } from "lucide-react";
import { toast } from "sonner";
import GstPortalLaunch from "@/components/clients/GstPortalLaunch";
import GstArchive from "@/components/GstArchive";

export default function Tasks() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [empF, setEmpF] = useState("all");
  const [prioF, setPrioF] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payTask, setPayTask] = useState(null);
  const [vault, setVault] = useState({ allowed: false, items: {} });
  const [view, setView] = useState("open");

  const loadVault = async () => { try { setVault((await api.get("/gst-vault/status")).data); } catch { /* non-privileged */ } };
  const load = async () => {
    const [t, c, e, s] = await Promise.all([
      api.get("/tasks"),
      api.get("/clients"),
      api.get("/employees"),
      api.get("/catalog/services"),
    ]);
    setTasks(t.data); setClients(c.data); setEmployees(e.data); setServices(s.data.services);
    loadVault();
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return tasks.filter(t => {
      if (statusF !== "all" && t.status !== statusF) return false;
      if (empF !== "all" && t.assigned_employee_id !== empF) return false;
      if (prioF !== "all" && t.priority !== prioF) return false;
      if (q && ![t.client_name, t.service, t.description, t.remarks].join(" ").toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [tasks, statusF, empF, prioF, q]);

  const markComplete = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: "Completed", accuracy_score: 92, satisfaction_score: 90 });
    toast.success("Marked completed");
    load();
  };
  const startTask = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: "In Progress" });
    load();
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete task for ${t.client_name}?`)) return;
    await api.delete(`/tasks/${t.id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Task Sheet · {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</div>
          <h1 className="font-heading font-black tracking-tight text-4xl mt-2">{isAdmin ? "Daily Tasks" : "My Tasks"}</h1>
        </div>
        {isAdmin ? (
          <Button data-testid="new-task-btn" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
            <Plus size={16} className="mr-1" /> New Task
          </Button>
        ) : null}
      </header>

      <div className="flex gap-1 border-b border-zinc-200" data-testid="tasks-view-tabs">
        {[["open", "Open Tasks"], ["gst-archive", "GST Monthly Store"]].map(([k, l]) => (
          <button key={k} data-testid={`tasks-tab-${k}`} onClick={() => setView(k)} className={`px-4 py-2 text-xs font-semibold tracking-wide uppercase border-b-2 -mb-px transition-colors ${view === k ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>{l}</button>
        ))}
      </div>

      {view === "gst-archive" ? <GstArchive /> : (<>
      <div className="border border-zinc-200 bg-white p-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-1 text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500">
          <Filter size={13} /> Filters
        </div>
        <Input placeholder="Search client, service, remarks…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" data-testid="task-search-input" />
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["Pending", "In Progress", "Completed", "Overdue"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={prioF} onValueChange={setPrioF}>
          <SelectTrigger className="w-36" data-testid="filter-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {["High", "Medium", "Low"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={empF} onValueChange={setEmpF}>
          <SelectTrigger className={`w-56 ${isAdmin ? "" : "hidden"}`} data-testid="filter-employee"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All employees</SelectItem>
            {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-zinc-500 tabular">{filtered.length} of {tasks.length} tasks</div>
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2" data-testid="tasks-mobile-list">
        {filtered.map(t => (
          <div key={t.id} data-testid={`task-card-${t.id}`} className="bg-white border border-zinc-200 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{t.client_name}</div>
                <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1">
                  {t.service}
                  {t.high_risk ? <ShieldAlert size={11} className="text-rose-600 shrink-0" /> : null}
                </div>
                <GstPortalLaunch task={t} vault={vault} isAdmin={isAdmin} onChanged={loadVault} tid={`task-gst-${t.id}-m`} />
              </div>
              <div className="flex flex-col items-end gap-0.5 shrink-0">
                <PriorityBadge priority={t.priority} />
                <StatusBadge status={t.status} />
              </div>
            </div>
            {t.description ? <div className="mt-1.5 text-[11px] text-zinc-600 line-clamp-2">{t.description}</div> : null}
            <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
              <div><span className="text-zinc-400 uppercase tracking-wider text-[9px]">Assignee</span><div className="truncate">{t.assigned_employee_name}</div></div>
              <div><span className="text-zinc-400 uppercase tracking-wider text-[9px]">Deadline</span><div className="tabular">{(t.deadline || "").slice(0, 10)}</div></div>
              <div className="col-span-2"><span className="text-zinc-400 uppercase tracking-wider text-[9px]">Payment</span>
                <div><span data-testid={`payment-status-${t.id}-m`} className={`inline-flex mt-0.5 px-1.5 py-0.5 text-[10px] font-semibold ${t.payment_status === "Paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{t.payment_status || "Pending"}</span></div>
              </div>
              {t.documents_pending?.length ? <div className="col-span-2 text-amber-700"><span className="text-zinc-400 uppercase tracking-wider text-[9px] block">Docs pending</span>{t.documents_pending.join(", ")}</div> : null}
            </div>
            <div className="mt-2 pt-2 border-t border-zinc-100 flex flex-wrap gap-1 justify-end">
              {isAdmin && t.payment_status !== "Paid" ? (
                <button data-testid={`collect-payment-${t.id}-m`} onClick={() => { setPayTask(t); setPayOpen(true); }} className="p-1.5 hover:bg-emerald-50 rounded"><IndianRupee size={15} className="text-emerald-700" /></button>
              ) : null}
              {isAdmin ? <button data-testid={`invoice-task-${t.id}-m`} onClick={() => nav(`/invoices?task_id=${t.id}`)} className="p-1.5 hover:bg-zinc-100 rounded"><Receipt size={15} /></button> : null}
              {t.status !== "Completed" ? (
                <>
                  {t.status === "Pending" ? <button data-testid={`start-task-${t.id}-m`} onClick={() => startTask(t)} className="p-1.5 hover:bg-zinc-100 rounded text-[10px] font-bold tracking-widest">START</button> : null}
                  <button data-testid={`complete-task-${t.id}-m`} onClick={() => markComplete(t)} className="p-1.5 hover:bg-emerald-50 rounded"><CheckCircle2 size={15} className="text-emerald-700" /></button>
                </>
              ) : null}
              <button data-testid={`edit-task-${t.id}-m`} onClick={() => { setEditing(t); setDialogOpen(true); }} className="p-1.5 hover:bg-zinc-100 rounded"><Pencil size={15} /></button>
              {isAdmin ? <button data-testid={`delete-task-${t.id}-m`} onClick={() => remove(t)} className="p-1.5 hover:bg-rose-50 rounded ml-auto"><Trash2 size={15} className="text-rose-600" /></button> : null}
            </div>
          </div>
        ))}
        {!filtered.length && <div className="text-center py-8 text-sm text-zinc-500">No tasks match.</div>}
      </div>

      <div className="hidden sm:block border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 sticky top-0">
            <tr className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 border-b border-zinc-200">
              {["Prio", "Client", "Service", "Task", "Assignee", "Start", "Expected", "Deadline", "Status", "Payment", "Docs", "Actions"].map(h => (
                <th key={h} className="text-left font-semibold px-3 py-2.5 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="stripe-rows">
            {filtered.map(t => (
              <tr key={t.id} data-testid={`task-row-${t.id}`} className="border-t border-zinc-100 align-top">
                <td className="px-3 py-2.5"><PriorityBadge priority={t.priority} /></td>
                <td className="px-3 py-2.5 font-medium">{t.client_name}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    <span>{t.service}</span>
                    {t.high_risk ? <ShieldAlert size={13} className="text-rose-600" title="Statutory / high-risk" /> : null}
                  </div>
                  <GstPortalLaunch task={t} vault={vault} isAdmin={isAdmin} onChanged={loadVault} tid={`task-gst-${t.id}`} />
                </td>
                <td className="px-3 py-2.5 text-zinc-600 max-w-[240px]">{t.description}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">{t.assigned_employee_name}</td>
                <td className="px-3 py-2.5 tabular text-xs text-zinc-500 whitespace-nowrap">{(t.start_time || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2.5 tabular text-xs text-zinc-500 whitespace-nowrap">{(t.expected_completion || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2.5 tabular text-xs whitespace-nowrap">{(t.deadline || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2.5"><StatusBadge status={t.status} /></td>
                <td className="px-3 py-2.5">
                  <span
                    data-testid={`payment-status-${t.id}`}
                    className={`inline-flex px-2 py-0.5 text-[11px] font-semibold ${t.payment_status === "Paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
                  >
                    {t.payment_status || "Pending"}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-xs">
                  {t.documents_pending?.length
                    ? <span className="text-amber-700">Pending: {t.documents_pending.join(", ")}</span>
                    : <span className="text-zinc-400">—</span>}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    {isAdmin && t.payment_status !== "Paid" && (
                      <button
                        data-testid={`collect-payment-${t.id}`}
                        aria-label={`Collect payment for ${t.client_name}`}
                        onClick={() => { setPayTask(t); setPayOpen(true); }}
                        title="Collect payment"
                        className="p-1 hover:bg-emerald-50 rounded"
                      >
                        <IndianRupee size={15} className="text-emerald-700" />
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        data-testid={`invoice-task-${t.id}`}
                        aria-label={`Generate invoice for ${t.client_name}`}
                        onClick={() => nav(`/invoices?task_id=${t.id}`)}
                        title="Generate invoice"
                        className="p-1 hover:bg-zinc-100 rounded"
                      >
                        <Receipt size={15} />
                      </button>
                    )}
                    {t.status !== "Completed" && (
                      <>
                        {t.status === "Pending" ? (
                          <button data-testid={`start-task-${t.id}`} onClick={() => startTask(t)} title="Start" className="p-1 hover:bg-zinc-100 rounded">
                            <span className="text-[10px] font-bold tracking-widest">START</span>
                          </button>
                        ) : null}
                        <button data-testid={`complete-task-${t.id}`} onClick={() => markComplete(t)} title="Mark completed" className="p-1 hover:bg-emerald-50 rounded">
                          <CheckCircle2 size={16} className="text-emerald-700" />
                        </button>
                      </>
                    )}
                    <button data-testid={`edit-task-${t.id}`} onClick={() => { setEditing(t); setDialogOpen(true); }} title={isAdmin ? "Edit" : "Open · attach docs"} className="p-1 hover:bg-zinc-100 rounded">
                      <Pencil size={15} />
                    </button>
                    {isAdmin ? (
                      <button data-testid={`delete-task-${t.id}`} onClick={() => remove(t)} title="Delete" className="p-1 hover:bg-rose-50 rounded">
                        <Trash2 size={15} className="text-rose-600" />
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={12} className="text-center py-12 text-sm text-zinc-500">No tasks match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      </>)}

      <TaskDialog open={dialogOpen} onOpenChange={setDialogOpen} clients={clients} employees={employees} services={services} initial={editing} onSaved={load} />
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} task={payTask} onPaid={load} />
    </div>
  );
}
